<template>
  <div class="p-8 bg-[#FAFBFC] min-h-screen">
    <header class="mb-10">
      <h1 class="text-3xl font-extrabold text-[#222222] tracking-tight">{{ t.title }}</h1>
      <p class="text-gray-500 font-medium">{{ t.subtitle }}</p>
    </header>

    <div class="grid lg:grid-cols-12 gap-8">
      
      <div class="lg:col-span-3 space-y-2">
        <button v-for="tab in tabs" :key="tab.id" 
                @click="activeTab = tab.id"
                :class="activeTab === tab.id ? 'bg-white border-dimotec-orange text-dimotec-orange shadow-sm' : 'bg-transparent border-transparent text-gray-500 hover:bg-gray-100'"
                class="w-full flex items-center gap-3 px-6 py-4 border-l-4 rounded-r-xl font-bold transition-all text-sm">
          <component :is="tab.icon" class="w-5 h-5" />
          {{ tab.label }}
        </button>
      </div>

      <div class="lg:col-span-9 space-y-8">
        
        <div v-if="activeTab === 'general'" class="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100">
          <h2 class="text-xl font-black mb-6">{{ t.platformIdentity }}</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="space-y-4">
              <label class="block text-xs font-black uppercase tracking-widest text-gray-400">{{ t.saasInstanceName }}</label>
              <input type="text" v-model="settings.siteName" class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 focus:ring-2 focus:ring-dimotec-orange/20 font-medium">
            </div>
            <div class="space-y-4">
              <label class="block text-xs font-black uppercase tracking-widest text-gray-400">{{ t.supportUrl }}</label>
              <input type="text" v-model="settings.supportUrl" class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 focus:ring-2 focus:ring-dimotec-orange/20 font-medium">
            </div>
          </div>
          <div class="mt-8">
            <label class="block text-xs font-black uppercase tracking-widest text-gray-400 mb-4">{{ t.superAdminLogo }}</label>
            <div class="flex items-center gap-6 p-6 border-2 border-dashed border-gray-100 rounded-2xl">
              <div class="w-20 h-20 bg-gray-50 rounded-2xl flex items-center justify-center">
                <span class="text-3xl font-black text-gray-200">DF</span>
              </div>
              <button class="px-6 py-2 bg-gray-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest">{{ t.replaceLogo }}</button>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'notifications'" class="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100">
          <h2 class="text-xl font-black mb-6">{{ t.smtpConfig }}</h2>
          <div class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div class="md:col-span-2 space-y-2">
                <label class="text-xs font-bold text-gray-500">{{ t.smtpHost }}</label>
                <input type="text" placeholder="smtp.postmarkapp.com" class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 text-sm">
              </div>
              <div class="space-y-2">
                <label class="text-xs font-bold text-gray-500">{{ t.port }}</label>
                <input type="text" placeholder="587" class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 text-sm">
              </div>
            </div>
            <div class="flex items-center gap-4 p-4 bg-blue-50 text-blue-700 rounded-2xl">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <p class="text-xs font-medium">{{ t.emailUsageInfo }}</p>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'security'" class="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100">
          <h2 class="text-xl font-black mb-6">{{ t.dataSecurity }}</h2>
          <div class="divide-y divide-gray-50">
            <div class="py-6 flex justify-between items-center">
              <div>
                <p class="text-sm font-bold text-gray-900">{{ t.ticketAnonymization }}</p>
                <p class="text-xs text-gray-400">{{ t.deleteNamesAfter24h }}</p>
              </div>
              <div class="w-12 h-6 bg-green-500 rounded-full flex items-center px-1">
                <div class="w-4 h-4 bg-white rounded-full ml-auto"></div>
              </div>
            </div>
            <div class="py-6 flex justify-between items-center">
              <div>
                <p class="text-sm font-bold text-gray-900">{{ t.forceHttps }}</p>
                <p class="text-xs text-gray-400">{{ t.rejectInvalidCert }}</p>
              </div>
              <div class="w-12 h-6 bg-gray-200 rounded-full flex items-center px-1">
                <div class="w-4 h-4 bg-white rounded-full"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-between items-center bg-gray-900 rounded-[2rem] p-6 shadow-xl">
          <p class="text-white text-xs font-medium italic opacity-60">{{ t.lastModified }}</p>
          <div class="flex gap-4">
            <button class="px-6 py-3 text-white/50 text-xs font-black uppercase tracking-widest hover:text-white transition">{{ t.cancel }}</button>
            <button class="px-8 py-3 bg-blue-500 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-900/20 active:scale-95 transition-all">
              {{ t.saveSettings }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      activeTab: 'general',
      currentLanguage: localStorage.getItem("appLanguage") || "fr",
      translations: {
        fr: {
          title: "Paramètres Système",
          subtitle: "Configuration globale du moteur Datafuse et des services tiers.",
          generalBranding: "Général & Branding",
          notificationsSmtp: "Notifications & SMTP",
          securityGdpr: "Sécurité & RGPD",
          webhooksApiKeys: "Webhooks & API Keys",
          databaseBackups: "Sauvegardes Database",
          platformIdentity: "Identité de la Plateforme",
          saasInstanceName: "Nom de l'Instance SaaS",
          supportUrl: "URL de Support (Support Link)",
          superAdminLogo: "Logo de la Console SuperAdmin",
          replaceLogo: "Remplacer le logo",
          smtpConfig: "Configuration Mails (SMTP)",
          smtpHost: "Serveur Sortant (Host)",
          port: "Port",
          emailUsageInfo: "Les mails servent aux alertes \"Papier bas\" et aux réinitialisations de mot de passe clients.",
          dataSecurity: "Sécurité des Données",
          ticketAnonymization: "Anonymisation des Tickets (RGPD)",
          deleteNamesAfter24h: "Supprimer les noms clients après 24h.",
          forceHttps: "Forcer le HTTPS sur les TV",
          rejectInvalidCert: "Refuse la connexion si le certificat n'est pas valide.",
          lastModified: "Modifié il y a 3 minutes par SuperAdmin",
          cancel: "Annuler",
          saveSettings: "Sauvegarder les réglages"
        },
        en: {
          title: "System Settings",
          subtitle: "Global configuration of Datafuse engine and third-party services.",
          generalBranding: "General & Branding",
          notificationsSmtp: "Notifications & SMTP",
          securityGdpr: "Security & GDPR",
          webhooksApiKeys: "Webhooks & API Keys",
          databaseBackups: "Database Backups",
          platformIdentity: "Platform Identity",
          saasInstanceName: "SaaS Instance Name",
          supportUrl: "Support URL (Support Link)",
          superAdminLogo: "SuperAdmin Console Logo",
          replaceLogo: "Replace logo",
          smtpConfig: "Mail Configuration (SMTP)",
          smtpHost: "Outgoing Server (Host)",
          port: "Port",
          emailUsageInfo: "Emails are used for \"Low Paper\" alerts and customer password resets.",
          dataSecurity: "Data Security",
          ticketAnonymization: "Ticket Anonymization (GDPR)",
          deleteNamesAfter24h: "Delete customer names after 24h.",
          forceHttps: "Force HTTPS on TVs",
          rejectInvalidCert: "Reject connection if certificate is invalid.",
          lastModified: "Modified 3 minutes ago by SuperAdmin",
          cancel: "Cancel",
          saveSettings: "Save settings"
        },
        de: {
          title: "Systemeinstellungen",
          subtitle: "Globale Konfiguration des Datafuse-Motors und Drittanbieterdienste.",
          generalBranding: "Allgemein & Branding",
          notificationsSmtp: "Benachrichtigungen & SMTP",
          securityGdpr: "Sicherheit & DSGVO",
          webhooksApiKeys: "Webhooks & API-Schlüssel",
          databaseBackups: "Datenbank-Backups",
          platformIdentity: "Plattform-Identität",
          saasInstanceName: "SaaS-Instanzname",
          supportUrl: "Support-URL (Support-Link)",
          superAdminLogo: "SuperAdmin-Konsole Logo",
          replaceLogo: "Logo ersetzen",
          smtpConfig: "E-Mail-Konfiguration (SMTP)",
          smtpHost: "Ausgehender Server (Host)",
          port: "Port",
          emailUsageInfo: "E-Mails werden für \"Wenig Papier\"-Warnungen und Kunden-Passwort-Zurücksetzungen verwendet.",
          dataSecurity: "Datensicherheit",
          ticketAnonymization: "Ticket-Anonymisierung (DSGVO)",
          deleteNamesAfter24h: "Kundennamen nach 24h löschen.",
          forceHttps: "HTTPS auf TVs erzwingen",
          rejectInvalidCert: "Verbindung ablehnen, wenn Zertifikat ungültig ist.",
          lastModified: "Vor 3 Minuten von SuperAdmin geändert",
          cancel: "Abbrechen",
          saveSettings: "Einstellungen speichern"
        },
        it: {
          title: "Impostazioni Sistema",
          subtitle: "Configurazione globale del motore Datafuse e servizi di terze parti.",
          generalBranding: "Generale & Branding",
          notificationsSmtp: "Notifiche & SMTP",
          securityGdpr: "Sicurezza & GDPR",
          webhooksApiKeys: "Webhooks & Chiavi API",
          databaseBackups: "Backup Database",
          platformIdentity: "Identità Piattaforma",
          saasInstanceName: "Nome Istanza SaaS",
          supportUrl: "URL Supporto (Link Supporto)",
          superAdminLogo: "Logo Console SuperAdmin",
          replaceLogo: "Sostituisci logo",
          smtpConfig: "Configurazione Mail (SMTP)",
          smtpHost: "Server In Uscita (Host)",
          port: "Porta",
          emailUsageInfo: "Le email vengono utilizzate per avvisi \"Carta bassa\" e reimpostazioni password clienti.",
          dataSecurity: "Sicurezza Dati",
          ticketAnonymization: "Anonimizzazione Ticket (GDPR)",
          deleteNamesAfter24h: "Elimina nomi clienti dopo 24h.",
          forceHttps: "Forza HTTPS sulle TV",
          rejectInvalidCert: "Rifiuta connessione se certificato non valido.",
          lastModified: "Modificato 3 minuti fa da SuperAdmin",
          cancel: "Annulla",
          saveSettings: "Salva impostazioni"
        }
      },
      settings: {
        siteName: 'Datafuse Automation Suite',
        supportUrl: 'https://support.datafuse.io'
      }
    }
  },
  computed: {
    t() {
      return this.translations[this.currentLanguage] || this.translations.fr;
    },
    tabs() {
      return [
        { id: 'general', label: this.t.generalBranding, icon: 'SettingsIcon' },
        { id: 'notifications', label: this.t.notificationsSmtp, icon: 'BellIcon' },
        { id: 'security', label: this.t.securityGdpr, icon: 'ShieldCheckIcon' },
        { id: 'api', label: this.t.webhooksApiKeys, icon: 'KeyIcon' },
        { id: 'backup', label: this.t.databaseBackups, icon: 'DatabaseIcon' }
      ];
    }
  }
}
</script>